import { createFileRoute } from '@tanstack/react-router';
import { Code, Group, Modal as MModal, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { Ban, CheckCircle2, RotateCcw, Search, Trash2, Upload, UserPlus, Users, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageContainer,
  PageHeader,
  Section,
  Table,
  type TableColumn,
} from '../components/ui';
import { cardItem, staggerContainer } from '../lib/motion';
import { ErrorState } from '../components/common/ErrorState';
import { LoadingState } from '../components/common/LoadingState';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import { useDeleteUser, useDisableUser, useEnableUser, useInviteUser, useTenants, useUsers, type AdminUser } from '../hooks/useAdmin';

/** Role rank so an admin can only disable strictly-lower-ranked users (matches the backend guard). */
const ROLE_RANK: Record<string, number> = { viewer: 0, trainee: 0, instructor: 1, org_admin: 2, superadmin: 3 };
import { toast } from '../store/useToasts';
import type { UserRole } from '../store/useAuthStore';

export const Route = createFileRoute('/admin/users')({
  component: AdminUsers,
});

const BULK_SAMPLE = `first_name,last_name,email
Alice,Anderson,alice@university.edu
Bob,Brown,bob@university.edu`;

interface BulkRow {
  firstName: string;
  lastName: string;
  email: string;
}

// Parse pasted CSV of first_name,last_name,email. Tolerates a header row, blank
// lines, quoted values, and rows with only an email. Returns rows + line errors.
function parseBulkCsv(text: string): { rows: BulkRow[]; errors: string[] } {
  const rows: BulkRow[] = [];
  const errors: string[] = [];
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  lines.forEach((line, i) => {
    const cells = line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    // Skip an obvious header row.
    if (i === 0 && cells.some((c) => c.toLowerCase() === 'email') && !cells.some((c) => emailRe.test(c))) {
      return;
    }
    // Find the email cell wherever it sits; the other two cells are the names.
    const emailCell = cells.find((c) => emailRe.test(c));
    if (!emailCell) {
      errors.push(`Line ${i + 1}: no valid email found — skipped`);
      return;
    }
    const names = cells.filter((c) => c !== emailCell);
    rows.push({ firstName: names[0] ?? '', lastName: names[1] ?? '', email: emailCell.toLowerCase() });
  });
  // De-duplicate by email, keeping the first occurrence.
  const seen = new Set<string>();
  const deduped = rows.filter((r) => (seen.has(r.email) ? false : (seen.add(r.email), true)));
  return { rows: deduped, errors };
}

function renderEmpty(q: string, canManage: boolean, clearFilter: () => void, onInvite: () => void) {
  if (q) {
    return (
      <EmptyState
        illustration={<Users size={48} />}
        title="No users match your filter"
        description="Try clearing the search box to see all users in this tenant."
        cta={{ label: 'Clear filter', onClick: clearFilter, variant: 'secondary' }}
      />
    );
  }
  if (canManage) {
    return (
      <EmptyState
        illustration={<Users size={48} />}
        title="No users yet"
        description="Invite the first user to begin using your tenant."
        cta={{ label: 'Invite user', onClick: onInvite }}
      />
    );
  }
  return (
    <EmptyState
      illustration={<Users size={48} />}
      title="No users yet"
      description="No users have been provisioned in your tenant."
    />
  );
}

export function AdminUsers() {
  const [q, setQ] = useState('');
  const role = useAuthStore(selectRole);
  const canManage = role === 'instructor' || role === 'org_admin' || role === 'superadmin';
  const isSuper = role === 'superadmin';
  const { data, isPending, isError, error, refetch } = useUsers();
  const disable = useDisableUser();
  const enable = useEnableUser();
  const del = useDeleteUser();
  const [confirmDisableId, setConfirmDisableId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const tenants = useTenants();
  const invite = useInviteUser();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // A superadmin may mint org admins for any org; an org admin only adds
  // instructors/trainees inside their own org.
  const [inviteRole, setInviteRole] = useState<UserRole>(isSuper ? 'org_admin' : role === 'instructor' ? 'trainee' : 'instructor');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const roleOptions = isSuper
    ? [{ value: 'org_admin', label: 'Org admin' }, { value: 'instructor', label: 'Instructor' }, { value: 'trainee', label: 'Trainee' }]
    : role === 'instructor'
    ? [{ value: 'trainee', label: 'Trainee' }]
    : [{ value: 'instructor', label: 'Instructor' }, { value: 'trainee', label: 'Trainee' }];
  // org admins + instructors add people into their OWN org (backend infers the
  // tenant from their token); only superadmin picks the org explicitly.

  const submitInvite = () => {
    if (!email.trim()) { toast.error('Email is required'); return; }
    if (isSuper && !tenantId) { toast.error('Choose the organization to add this user to'); return; }
    invite.mutate(
      {
        email: email.trim(), role: inviteRole,
        ...(isSuper && tenantId ? { tenantId } : {}),
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
      },
      {
        onSuccess: () => { toast.success('Invitation sent'); setOpen(false); setEmail(''); setFirstName(''); setLastName(''); },
        onError: (e) => toast.error(e.message || 'Could not invite the user'),
      },
    );
  };

  // --- Bulk CSV invite ---
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkRole, setBulkRole] = useState<UserRole>('trainee');
  const [bulkTenantId, setBulkTenantId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResults, setBulkResults] = useState<{ email: string; ok: boolean; error?: string }[] | null>(null);
  const parsed = useMemo(() => parseBulkCsv(bulkText), [bulkText]);

  const runBulkInvite = async () => {
    if (parsed.rows.length === 0) { toast.error('Paste at least one row with a valid email.'); return; }
    if (isSuper && !bulkTenantId) { toast.error('Choose the organization to add these users to'); return; }
    setBulkBusy(true);
    setBulkResults(null);
    const results: { email: string; ok: boolean; error?: string }[] = [];
    for (const row of parsed.rows) {
      try {
        await invite.mutateAsync({
          email: row.email, role: bulkRole,
          ...(isSuper && bulkTenantId ? { tenantId: bulkTenantId } : {}),
          ...(row.firstName ? { firstName: row.firstName } : {}),
          ...(row.lastName ? { lastName: row.lastName } : {}),
        });
        results.push({ email: row.email, ok: true });
      } catch (e) {
        results.push({ email: row.email, ok: false, error: e instanceof Error ? e.message : 'failed' });
      }
      setBulkResults([...results]);
    }
    setBulkBusy(false);
    const ok = results.filter((r) => r.ok).length;
    if (ok > 0) toast.success(`Invited ${ok} of ${results.length} user${results.length === 1 ? '' : 's'}`);
    if (ok < results.length) toast.error(`${results.length - ok} could not be invited — see the list`);
    void refetch();
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const lower = q.trim().toLowerCase();
    if (!lower) return data;
    return data.filter((u) => u.email.toLowerCase().includes(lower) || u.role.includes(lower));
  }, [data, q]);

  const columns: ReadonlyArray<TableColumn<AdminUser>> = [
    { key: 'email', header: 'Email', cell: (u) => <Text className="font-mono" size="sm" c="rgb(var(--c-txt-bright))">{u.email}</Text> },
    { key: 'role', header: 'Role', cell: (u) => <Badge variant={u.role === 'org_admin' ? 'warn' : 'info'} size="xs">{u.role}</Badge> },
    { key: 'mfa', header: 'MFA', cell: (u) => <Badge variant={u.mfaEnabled ? 'success' : 'danger'} size="xs">{u.mfaEnabled ? 'enabled' : 'off'}</Badge> },
    { key: 'last', header: 'Last seen', cell: (u) => <Text size="xs" c="dimmed" className="font-mono">{u.lastSeen}</Text> },
    {
      key: 'status',
      header: 'Status',
      cell: (u) => (
        <Badge variant={u.status === 'active' ? 'success' : u.status === 'invited' ? 'info' : 'danger'} size="xs">
          {u.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (u) => {
        // Only staff can manage, and only strictly-lower-ranked users (matches
        // the backend guard) — so you can't disable a peer, a higher role, or yourself.
        const lower = (ROLE_RANK[u.role] ?? 0) < (ROLE_RANK[role ?? ''] ?? 0);
        if (!canManage || !lower) return null;

        // Delete is destructive/irreversible → its own explicit confirm that
        // takes over the cell.
        if (confirmDeleteId === u.id) {
          return (
            <Group gap={4} wrap="nowrap" justify="flex-end">
              <Button
                variant="danger"
                size="xs"
                leftIcon={<Trash2 size={12} />}
                loading={del.isPending && del.variables?.userId === u.id}
                onClick={() => {
                  del.mutate({ userId: u.id });
                  setConfirmDeleteId(null);
                }}
              >
                Delete permanently
              </Button>
              <Button variant="ghost" size="xs" onClick={() => setConfirmDeleteId(null)}>
                Cancel
              </Button>
            </Group>
          );
        }

        if (confirmDisableId === u.id) {
          return (
            <Group gap={4} wrap="nowrap" justify="flex-end">
              <Button
                variant="danger"
                size="xs"
                loading={disable.isPending && disable.variables?.userId === u.id}
                onClick={() => {
                  disable.mutate({ userId: u.id });
                  setConfirmDisableId(null);
                }}
              >
                Confirm
              </Button>
              <Button variant="ghost" size="xs" onClick={() => setConfirmDisableId(null)}>
                Cancel
              </Button>
            </Group>
          );
        }

        // Default: disable/re-enable + delete.
        return (
          <Group gap={4} wrap="nowrap" justify="flex-end">
            {u.status === 'disabled' ? (
              <Button
                variant="ghost"
                size="xs"
                leftIcon={<RotateCcw size={12} />}
                loading={enable.isPending && enable.variables?.userId === u.id}
                onClick={() => enable.mutate({ userId: u.id })}
              >
                Re-enable
              </Button>
            ) : (
              <Button variant="ghost" size="xs" leftIcon={<Ban size={12} />} onClick={() => setConfirmDisableId(u.id)}>
                Disable
              </Button>
            )}
            <Button
              variant="ghost"
              size="xs"
              leftIcon={<Trash2 size={12} />}
              onClick={() => setConfirmDeleteId(u.id)}
              title="Permanently delete this user"
            >
              Delete
            </Button>
          </Group>
        );
      },
    },
  ];

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Users}
        title="Users"
        description="Platform users, their roles and tenant assignments."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Admin', to: '/admin' }, { label: 'Users' }]}
        actions={
          canManage ? (
            <Group gap="xs">
              <Button variant="secondary" leftIcon={<Upload size={14} />} onClick={() => { setBulkResults(null); setBulkOpen(true); }}>
                Bulk invite
              </Button>
              <Button variant="primary" leftIcon={<UserPlus size={14} />} onClick={() => setOpen(true)}>
                Invite user
              </Button>
            </Group>
          ) : undefined
        }
      />

      <Section
        title="Directory"
        description={role === 'instructor' ? 'View your organization\u2019s users and invite trainees.' : 'Manage roles, invite new users, and require MFA before sign-in.'}
      >
        <Stack gap="md">
          <TextInput
            leftSection={<Search size={14} />}
            placeholder="Filter by email or role"
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            style={{ maxWidth: 400 }}
            aria-label="Filter users"
          />

          {isError ? (
            <ErrorState
              title="Could not load users"
              message={(error as Error)?.message ?? 'Unknown error'}
              onRetry={() => void refetch()}
            />
          ) : isPending ? (
            <LoadingState rows={4} height={32} />
          ) : filtered.length === 0 ? (
            renderEmpty(q, canManage, () => setQ(''), () => setOpen(true))
          ) : (
            <motion.div variants={staggerContainer} initial="hidden" animate="show">
              <motion.div variants={cardItem}>
                <Card>
                  <Stack gap="md">
                    <Group gap="xs">
                      <Users size={16} />
                      <Text fw={600} size="sm">
                        {filtered.length} users
                      </Text>
                    </Group>
                    <Table columns={columns} rows={filtered} rowKey={(u) => u.id} />
                  </Stack>
                </Card>
              </motion.div>
            </motion.div>
          )}
        </Stack>
      </Section>

      <MModal opened={open} onClose={() => setOpen(false)} title="Invite user" centered>
        <Stack gap="md">
          <TextInput label="Email" type="email" placeholder="person@org.edu" value={email}
            onChange={(e) => setEmail(e.currentTarget.value.trim())} required data-autofocus />
          <Group grow>
            <TextInput label="First name" value={firstName} onChange={(e) => setFirstName(e.currentTarget.value)} />
            <TextInput label="Last name" value={lastName} onChange={(e) => setLastName(e.currentTarget.value)} />
          </Group>
          <Select label="Role" data={roleOptions} value={inviteRole}
            onChange={(v) => setInviteRole((v ?? 'instructor') as UserRole)} allowDeselect={false} />
          {isSuper ? (
            <Select label="Organization" placeholder="Choose an org"
              data={(tenants.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
              value={tenantId} onChange={setTenantId} searchable
              description="Superadmin only: which org this user belongs to." />
          ) : null}
          <Group justify="flex-end" gap="sm">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={invite.isPending}>Cancel</Button>
            <Button variant="primary" onClick={submitInvite} loading={invite.isPending}>Send invite</Button>
          </Group>
        </Stack>
      </MModal>

      <MModal opened={bulkOpen} onClose={() => (bulkBusy ? null : setBulkOpen(false))} title="Bulk invite users" centered size="lg">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Paste one user per line as <Code>first_name,last_name,email</Code>. A header row is optional,
            and rows with just an email work too. Each person is emailed a temporary password.
          </Text>
          <Stack gap={4}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">Example</Text>
            <Code block>{BULK_SAMPLE}</Code>
          </Stack>
          <Textarea
            label="Users (CSV)"
            placeholder={BULK_SAMPLE}
            value={bulkText}
            onChange={(e) => setBulkText(e.currentTarget.value)}
            autosize minRows={5} maxRows={12}
            styles={{ input: { fontFamily: 'ui-monospace, monospace', fontSize: 13 } }}
            data-autofocus disabled={bulkBusy}
          />
          <Group grow>
            <Select label="Role for all" data={roleOptions} value={bulkRole}
              onChange={(v) => setBulkRole((v ?? 'trainee') as UserRole)} allowDeselect={false} disabled={bulkBusy} />
            {isSuper ? (
              <Select label="Organization" placeholder="Choose an org"
                data={(tenants.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
                value={bulkTenantId} onChange={setBulkTenantId} searchable disabled={bulkBusy} />
            ) : null}
          </Group>

          {parsed.errors.length > 0 ? (
            <Stack gap={2}>
              {parsed.errors.map((er) => (
                <Text key={er} size="xs" c="rgb(var(--c-hud-red))">{er}</Text>
              ))}
            </Stack>
          ) : null}

          {bulkResults ? (
            <Card>
              <Stack gap={6}>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                  {bulkResults.filter((r) => r.ok).length} invited · {bulkResults.filter((r) => !r.ok).length} failed
                </Text>
                {bulkResults.map((r) => (
                  <Group key={r.email} gap={8} wrap="nowrap">
                    {r.ok
                      ? <CheckCircle2 size={14} color="var(--mantine-color-teal-6, #16c65a)" />
                      : <XCircle size={14} color="#e5484d" />}
                    <Text size="xs" className="font-mono">{r.email}</Text>
                    {r.error ? <Text size="xs" c="dimmed">— {r.error}</Text> : null}
                  </Group>
                ))}
              </Stack>
            </Card>
          ) : null}

          <Group justify="space-between" gap="sm">
            <Text size="xs" c="dimmed">
              {parsed.rows.length} valid row{parsed.rows.length === 1 ? '' : 's'} ready
            </Text>
            <Group gap="sm">
              <Button variant="ghost" onClick={() => setBulkOpen(false)} disabled={bulkBusy}>Close</Button>
              <Button variant="primary" onClick={() => void runBulkInvite()} loading={bulkBusy} disabled={parsed.rows.length === 0}>
                {bulkBusy ? 'Inviting…' : `Invite ${parsed.rows.length || ''} user${parsed.rows.length === 1 ? '' : 's'}`}
              </Button>
            </Group>
          </Group>
        </Stack>
      </MModal>
    </PageContainer>
  );
}
